import { useMemo, type PropsWithChildren } from 'react';

import type { Unit } from '@dynatrace-sdk/units';

import { StoreProvider } from './StoreProvider.js';
import type { TruncationMode } from '../../../typography/text-ellipsis/TextEllipsis.js';
import { ChartSeriesActionContext } from '../../core/components/series-action/ChartSeriesActionContext.js';
import { ColorRuleContext } from '../../core/context/ColorRuleContext.js';
import type {
  ColorPalette,
  CustomColorPalette,
} from '../../core/types/color-palette.js';
import { ColorRuleProps } from '../../core/types/color-rule.js';
import type { ColoredRange } from '../../core/types/colored-range.js';
import type { LegendProps } from '../../core/types/legend.js';
import type { SeriesActionsTemplate } from '../../core/types/series-actions-template.js';
import type { TextSize } from '../../core/types/text-size.js';
import type { ChartToolbarConfig } from '../../core/types/toolbar.js';
import { ChartLegendContext } from '../context/ChartLegendContext.js';
import { ColorPaletteContext } from '../context/ColorPaletteContext.js';
import { FormatterContext } from '../context/FormatterContext.js';
import { LabelsDisplayContext } from '../context/LabelsDisplayContext.js';
import { MinMaxConfigContext } from '../context/MinMaxConfigContext.js';
import { NameAccessorContext } from '../context/NameAccessorContext.js';
import { TextSizeContext } from '../context/TextSizeContext.js';
import { ToolbarContext } from '../context/ToolbarContext.js';
import { TreeMapRawDataContext } from '../context/TreeMapRawDataContext.js';
import {
  DEFAULT_CUSTOM_TOOLTIP_CONFIG,
  TreeMapCustomTooltipContext,
} from '../contexts/TreeMapCustomTooltipContext.js';
import type { TreeMapFormatter } from '../types/internal.js';
import type { TreeMapCustomTooltipConfig } from '../types/treemap-chart.config.js';
import type {
  LabelsDisplay,
  NameAccessor,
  TreeMapData,
  TreeMapLeafNode,
} from '../types/treemap.js';

interface TreeMapChartProvidersProps extends PropsWithChildren {
  data: TreeMapData;
  seriesActions?: (node: TreeMapLeafNode) => SeriesActionsTemplate;
  formatter: TreeMapFormatter;
  truncationMode: TruncationMode;
  legend: LegendProps;
  toolbar?: ChartToolbarConfig;
  labelsDisplay: LabelsDisplay;
  nameAccessor?: NameAccessor;
  colorPalette: ColorPalette | CustomColorPalette | ColoredRange[];
  min?: 'data-min' | number;
  max?: 'data-max' | number;
  colorRules?: ColorRuleProps[];
  unit?: string | Unit;
  textSize: TextSize;
  tooltip?: TreeMapCustomTooltipConfig;
}

export const TreeMapChartProviders = (props: TreeMapChartProvidersProps) => {
  const {
    data,
    seriesActions,
    formatter,
    truncationMode,
    legend,
    toolbar,
    labelsDisplay,
    nameAccessor,
    colorPalette,
    min: configMin,
    max: configMax,
    children,
    colorRules = [],
    unit,
    textSize,
    tooltip,
  } = props;

  const min = configMin === 'data-min' ? undefined : configMin;
  const max = configMax === 'data-max' ? undefined : configMax;

  const memoizedRawData = useMemo(
    () => ({ ...data, unit: unit ?? data.unit }),
    [data, unit],
  );

  return (
    <ColorRuleContext.Provider value={colorRules}>
      <TreeMapRawDataContext.Provider value={memoizedRawData}>
        <ChartSeriesActionContext.Provider
          value={{
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore Strict mode transition - needs to be investigated
            chartSeriesActions: seriesActions,
          }}
        >
          <FormatterContext.Provider
            value={{
              formatter,
              truncationMode,
            }}
          >
            <ColorPaletteContext.Provider value={colorPalette}>
              <ChartLegendContext.Provider value={legend}>
                <NameAccessorContext.Provider value={nameAccessor}>
                  <ToolbarContext.Provider value={toolbar}>
                    <LabelsDisplayContext.Provider value={labelsDisplay}>
                      <TextSizeContext.Provider value={textSize}>
                        <MinMaxConfigContext.Provider value={{ min, max }}>
                          <TreeMapCustomTooltipContext.Provider
                            value={tooltip ?? DEFAULT_CUSTOM_TOOLTIP_CONFIG}
                          >
                            <StoreProvider>{children}</StoreProvider>
                          </TreeMapCustomTooltipContext.Provider>
                        </MinMaxConfigContext.Provider>
                      </TextSizeContext.Provider>
                    </LabelsDisplayContext.Provider>
                  </ToolbarContext.Provider>
                </NameAccessorContext.Provider>
              </ChartLegendContext.Provider>
            </ColorPaletteContext.Provider>
          </FormatterContext.Provider>
        </ChartSeriesActionContext.Provider>
      </TreeMapRawDataContext.Provider>
    </ColorRuleContext.Provider>
  );
};

TreeMapChartProviders['displayName'] = 'TreeMapChartProviders';
